import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { RiCloseFill, RiExternalLinkLine } from "react-icons/ri";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100vh);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100vh);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 1001;
  opacity: ${(props) => (props.$isClosing ? 0 : 1)};
  transition: opacity 0.5s ease;
  overflow: hidden;
`;

const PopupContainer = styled.div`
  background: white;
  border: 1.7px dashed black;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  padding: 50px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  width: 800px;
  max-height: 80vh;
  text-align: left;
  position: relative;
  margin: 20px;
  animation: ${(props) => (props.$isClosing ? slideOut : slideIn)} 0.5s ease-out;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 50px;
  padding-right: 30px;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #bbb;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 220px;
  margin-top: 10px;
`;

const HeaderRight = styled.div`
  flex-shrink: 0;
  margin-left: 30px;
  margin-right: 20px;
  max-width: 300px;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    margin-top: 20px;
  }
`;

const Thumbnail = styled.img`
  width: 300px;
  height: auto;
  padding: 0px;
  object-fit: contain;
  border-radius: 15px;
  border: 1.5px dashed;
  display: block;
`;

const TechList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 25px 0 16px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TechItem = styled.li`
  background-color: #eeeeee;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const TitleRow = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  display: block;
  word-break: break-word;
`;

const IconWrapper = styled.span`
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InfoLinkIcon = styled.a`
  color: black;
  font-size: 1.1rem;
  margin-top: 9px;
  display: inline-flex;
  align-items: center;
  transition: transform 100ms ease;

  &:hover {
    transform: scale(1.1);
  }

  svg {
    width: 1em;
    height: 1em;
  }
`;

const PopupText = styled.p`
  font-size: 1rem;
`;

const CloseButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 15px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;

    &:hover {
    transition: transform 100ms ease;
      transform: scale(1.1);
    }
  }
`;

const Popup = ({ title, text, onClose, thumbnail = "", technologies = [], link = "" }) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "";
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 600);
  };

  const getThumbnailSrc = (thumbnailName) => {
    if (!thumbnailName) return null;
    try {
      const images = require.context('../img/project-thumbnails', false, /\.(png|jpe?g|svg)$/);
      return images(`./${thumbnailName}`);
    } catch (error) {
      console.error(`Error loading image: ${thumbnailName}`, error);
      return null;
    }
  };

  const renderTitleWithLink = () => {
    if (!link) return title;

    const words = title.trim().split(" ");
    const lastWord = words.pop();

    return (
      <>
        {words.join(" ")}{" "}
        <IconWrapper>
          {lastWord}
          <InfoLinkIcon href={link} target="_blank" rel="noopener noreferrer">
            <RiExternalLinkLine />
          </InfoLinkIcon>
        </IconWrapper>
      </>
    );
  };

  return (
    <Overlay $isClosing={isClosing} onClick={handleClose}>
      <PopupContainer $isClosing={isClosing} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <RiCloseFill size={16} />
        </CloseButton>
        <ContentWrapper>
          <Header>
            <HeaderLeft>
              <TitleRow>{renderTitleWithLink()}</TitleRow>
              {technologies.length > 0 && (
                <TechList>
                  {technologies.map((tech, index) => (
                    <TechItem key={index}>{tech}</TechItem>
                  ))}
                </TechList>
              )}
            </HeaderLeft>
            {thumbnail && (
              <HeaderRight>
                <Thumbnail src={getThumbnailSrc(thumbnail)} alt="Thumbnail" />
              </HeaderRight>
            )}
          </Header>
          <PopupText>{text}</PopupText>
        </ContentWrapper>
      </PopupContainer>
    </Overlay>
  );
};

export default Popup;
